import {Component, OnDestroy, OnInit} from '@angular/core';
import {configVariableValues} from '@shared/constants/config-variable-values.constants';
import {AppConfigService} from '@shared/services/app-config.service';
import {ResponsiveService} from 'front-base-angular-core/angular/projects/front-base-angular-core/src/public-api';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import {StorageService} from '@core/src/public-api';

@Component({
  selector: 'si-pwa-install-banner',
  templateUrl: './pwa-install-banner.component.html',
  styleUrls: ['./pwa-install-banner.component.scss']
})
export class PwaInstallBannerComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  isDesktop: boolean;
  shouldBannerAppear: boolean;
  shouldBannerAppearRouteData: boolean;
  deferredPrompt;
  showBanner = false;
  postponeBannerTime: number;
  postponeBannerName = configVariableValues.DIRECT_ACCESS_TIMEOUT;
  private storageName = 'pasi-postpone-banner';

  constructor(
    private responsiveService: ResponsiveService,
    private appConfigService: AppConfigService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private storageService: StorageService
  ) {
    this.setLogic();
  }

  ngOnInit(): void {
    this.setupListenerBeforeInstallPrompt();
    this.setupListenerAppInstalled();
  }

  async setLogic(): Promise<void> {
    this.isDesktop = this.responsiveService.isDesktop();
    await this.getPostponeBannerTime();
    this.onNavigationEnd();
    this.subscriptions.push(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => this.onNavigationEnd())
    );
  }

  onNavigationEnd(): void {
    this.getRouteData();
    this.manageShouldBannerAppear();
  }

  getRouteData(): void {
    let routeSnapshot = this.activatedRoute.snapshot;
    while (routeSnapshot.firstChild) {
      routeSnapshot = routeSnapshot.firstChild;
    }

    const routeData = routeSnapshot.data;
    this.shouldBannerAppearRouteData = routeData?.pwaBannerShow;
  }

  manageShouldBannerAppear(force?: boolean): void {
    if (force === true || force === false) {
      this.shouldBannerAppear = force;
      return;
    }

    if (this.isDesktop || this.checkBannerIsPostponed() || !this.shouldBannerAppearRouteData) {
      this.shouldBannerAppear = false;
      return;
    }

    this.shouldBannerAppear = true;
  }

  async getPostponeBannerTime(): Promise<void> {
    await this.appConfigService.getConfigurations();
    const postponeBannerTime = this.appConfigService.getVariableValue(this.postponeBannerName);
    this.postponeBannerTime = parseInt(postponeBannerTime, 10);
  }

  setupListenerBeforeInstallPrompt(): void {
    window.addEventListener('beforeinstallprompt', (e) => {
      // Prevent default banner to appear
      e.preventDefault();
      // Save the event to launch it later
      this.deferredPrompt = e;
      // Show custom banner
      this.showBanner = true;
    });
  }

  setupListenerAppInstalled(): void {
    window.addEventListener('appinstalled', () => {
      this.manageShouldBannerAppear(false);
      this.showBanner = false;
      // Empty the variable as it can be deleted by the garbage collector
      this.deferredPrompt = null;
    });
  }

  async onClickBanner(): Promise<void> {
    this.showBanner = false;
    // Launch installation pop-up
    this.deferredPrompt.prompt();
    // Wait the user to answer
    const {outcome} = await this.deferredPrompt.userChoice;
    // As this event is already used, and we cannot use it again, it is removed
    this.deferredPrompt = null;
  }

  checkBannerIsPostponed(): boolean {
    const postponedDate = this.getBannerPostponeDateStorage();
    return postponedDate && (postponedDate + this.postponeBannerTime > new Date().getTime());
  }

  onClickCloseIcon(): void {
    this.showBanner = false;
    this.postponeBanner();
  }

  postponeBanner(): void {
    this.setBannerPostponeDateStorage(new Date().getTime());
  }

  getBannerPostponeDateStorage(): number {
    const date = this.storageService.getItem(this.storageName, true);
    return date ? parseInt(date, 10) : null;
  }

  setBannerPostponeDateStorage(date: number): void {
    this.storageService.setItem(this.storageName, date.toString(), true);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
